import { useState, useCallback, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { analyticsService, Restaurant as AnalyticsRestaurant } from '../services/analyticsService';
import { formatDateEST } from '../utils/timeUtils';

interface UseAnalyticsParams {
  accountId?: string;
  apiBaseUrl?: string;
}

interface UseAnalyticsReturn {
  franchises: AnalyticsRestaurant[];
  loading: boolean;
  fetchData: (start: Date, end: Date) => Promise<void>;
  fetchDataByString: (start: string, end: string) => Promise<void>;
}

/**
 * Custom hook for managing analytics data fetching
 * Centralizes API logic and duplicate prevention
 */
export function useAnalytics({ accountId, apiBaseUrl }: UseAnalyticsParams): UseAnalyticsReturn {
  const [franchises, setFranchises] = useState<AnalyticsRestaurant[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Ref to track the last fetched date range to prevent duplicate API calls
  const lastFetchedRangeRef = useRef<string>('');

  // Fetch data by date range string
  const fetchDataByString = useCallback(async (start: string, end: string) => {
    // Validate required parameters
    if (!apiBaseUrl) {
      console.error('❌ menuUploadCount validation failed: API Base URL missing');
      return;
    }
    
    if (!accountId) {
      console.error('❌ menuUploadCount validation failed: Account ID missing');
      return;
    }
    
    if (!start || !end) {
      console.error('❌ menuUploadCount validation failed: Start/End date missing');
      return;
    }
    
    // Create a unique key for this date range
    const rangeKey = `${start}-${end}`;
    
    // Skip if we already fetched this exact range
    if (lastFetchedRangeRef.current === rangeKey) {
      return;
    }
    
    lastFetchedRangeRef.current = rangeKey;
    setLoading(true);
    
    try {
      const result = await analyticsService.getMenuUploadCount(
        apiBaseUrl,
        accountId,
        start,
        end
      );
      
      setFranchises(result.response.franchises.data);
    } catch (error) {
      console.error('Error fetching menuUploadCount:', error);
      setFranchises([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, apiBaseUrl]);

  // Fetch data by Date objects
  const fetchData = useCallback(async (start: Date, end: Date) => {
    const startStr = formatDateEST(start) || format(start, 'yyyy-MM-dd');
    const endStr = formatDateEST(end) || format(end, 'yyyy-MM-dd');
    await fetchDataByString(startStr, endStr);
  }, [fetchDataByString]);

  // Fetch initial data on mount
  useEffect(() => {
    if (accountId && apiBaseUrl) {
      const today = format(new Date(), 'yyyy-MM-dd');
      fetchDataByString(today, today);
    }
  }, [accountId, apiBaseUrl, fetchDataByString]);

  return {
    franchises,
    loading,
    fetchData,
    fetchDataByString
  };
}
